import { useCallback, useEffect, useRef, useState } from 'react';
import type { VideoRecordingMode } from '../interfaces';

export interface IUseVideoAnswer {
  videoRef: React.RefObject<HTMLVideoElement>;
  fileInputRef: React.RefObject<HTMLInputElement>;
  mode: VideoRecordingMode;
  previewUrl: string | null;
  cameraError: string | null;
  recordingSecs: number;
  showPlayback: boolean;
  showLiveCamera: boolean;
  openCamera: () => Promise<void>;
  startRecording: () => void;
  stopRecording: () => void;
  handleRecordAgain: () => void;
  handleFileChange: (file: File | null) => void;
  triggerFilePicker: () => void;
}

interface IUseVideoAnswerParams {
  questionId: string;
  onCapture: (questionId: string, file: File, previewUrl: string) => void;
}

export const useVideoAnswer = ({ questionId, onCapture }: IUseVideoAnswerParams): IUseVideoAnswer => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [mode, setMode] = useState<VideoRecordingMode>('idle');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [recordingSecs, setRecordingSecs] = useState<number>(0);

  useEffect(() => {
    if (mode === 'recording') {
      setRecordingSecs(0);
      timerRef.current = setInterval(() => setRecordingSecs((s) => s + 1), 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [mode]);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stopStream();
    };
  }, [stopStream]);

  useEffect(() => {
    if ((mode === 'camera' || mode === 'recording') && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => undefined);
    }
  }, [mode]);

  const openCamera = async (): Promise<void> => {
    try {
      setCameraError(null);
      if (!streamRef.current) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        streamRef.current = stream;
      }
      setMode('camera');
    } catch (error) {
      setCameraError('Camera access failed. You can upload a video instead.');
    }
  };

  const startRecording = (): void => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const recorder = new MediaRecorder(streamRef.current);
    mediaRecorderRef.current = recorder;
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      const file = new File([blob], `prequal-${Date.now()}.webm`, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      stopStream();
      setPreviewUrl(url);
      setMode('idle');
      onCapture(questionId, file, url);
    };
    recorder.start();
    setMode('recording');
  };

  const stopRecording = (): void => {
    setMode('processing');
    mediaRecorderRef.current?.stop();
    mediaRecorderRef.current = null;
  };

  const handleRecordAgain = (): void => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    stopStream();
    void openCamera();
  };

  const handleFileChange = (file: File | null): void => {
    if (!file) return;
    stopStream();
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setMode('idle');
    onCapture(questionId, file, url);
  };

  const triggerFilePicker = (): void => {
    fileInputRef.current?.click();
  };

  const showPlayback = !!previewUrl && mode === 'idle';
  const showLiveCamera = mode === 'camera' || mode === 'recording';

  return {
    videoRef,
    fileInputRef,
    mode,
    previewUrl,
    cameraError,
    recordingSecs,
    showPlayback,
    showLiveCamera,
    openCamera,
    startRecording,
    stopRecording,
    handleRecordAgain,
    handleFileChange,
    triggerFilePicker,
  };
};
